import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SocketIoService, MdbToastService } from 'src/app/core/services';
import { MyPlayerService } from './my-player.service';

export interface MatchmakingPlayer {
  userID: number;
  username: string;
  smallProfilePicture?: string;
  premiumBadge?: boolean;
  isGuest?: boolean;
}

export interface MatchmakingState {
  isInQueue: boolean;
  numPlayers: number;
  searchStartTime?: Date;
  estimatedWaitTime?: number;
  foundPlayers?: MatchmakingPlayer[];
  playersFound?: number;
}

@Injectable({
  providedIn: 'root',
})
export class MatchmakingService {
  private _state = new BehaviorSubject<MatchmakingState>({
    isInQueue: false,
    numPlayers: 2,
  });

  constructor(
    private socketIO: SocketIoService,
    private myPlayer: MyPlayerService,
    private toast: MdbToastService
  ) {
    // Listen for matchmaking events from socket
    this.socketIO.on('matchmakingUpdate', (data) => {
      this.handleMatchmakingUpdate(data);
    });

    this.socketIO.on('matchFound', (data) => {
      this.handleMatchFound(data);
    });

    this.socketIO.on('partialMatch', (data) => {
      this.handlePartialMatch(data);
    });

    // Initialize matchmaking state from player data after login
    this.initFromPlayerData();
  }

  private async initFromPlayerData(): Promise<void> {
    try {
      await this.myPlayer.waitForInit();

      // Sync matchmaking state with player data after reload
      if (
        this.myPlayer.player.isInMatchmaking &&
        this.myPlayer.player.tableID
      ) {
        console.log('Restoring matchmaking state after reload');

        // Get table info to determine player count
        const table = this.myPlayer.player.table;
        const numPlayers = table?.maxPlayers || 2;

        // Restore matchmaking state
        this._state.next({
          isInQueue: true,
          numPlayers,
          searchStartTime: new Date(), // We don't know the actual start time after reload
          foundPlayers: [],
          playersFound: 0,
        });

        // Trigger initial partial match handling if we have table data
        if (table) {
          this.handlePartialMatch({
            data: {
              table,
              currentPlayers:
                table.members?.filter((m) => m && m.userID && m.userID !== 0)
                  .length || 0,
              maxPlayers: numPlayers,
              type: 'join',
            },
          });
        }
      }
    } catch (e) {
      console.warn('Failed to initialize matchmaking from player data:', e);
    }
  }

  get state$() {
    return this._state.asObservable();
  }

  get state() {
    return this._state.value;
  }

  async joinQueue(numPlayers: number = 2): Promise<void> {
    try {
      // Set matchmaking state IMMEDIATELY to prevent UI flicker
      this._state.next({
        isInQueue: true,
        numPlayers,
        searchStartTime: new Date(),
        foundPlayers: [],
        playersFound: 0,
      });

      // Leave any existing table first - same logic as training/lobby
      if (this.myPlayer.player?.tableID) {
        try {
          await this.socketIO.post('game/table/leave');
          await this.myPlayer.setMyTable(undefined);
          await this.myPlayer.update(); // Refresh player state
        } catch (leaveError) {
          console.warn('Failed to leave table before matchmaking:', leaveError);
          // Continue anyway - backend should handle this
        }
      }

      await this.socketIO.post('matchmaking/join', { numPlayers });
    } catch (e) {
      // Reset state on error
      this._state.next({
        isInQueue: false,
        numPlayers,
        foundPlayers: [],
        playersFound: 0,
      });
      this.toast.error('Fehler beim Beitreten der Matchmaking-Warteschlange');
      console.error('matchmaking/join failed:', e);
    }
  }

  async leaveQueue(): Promise<void> {
    try {
      await this.socketIO.post('matchmaking/leave');

      this._state.next({
        isInQueue: false,
        numPlayers: this.state.numPlayers,
        foundPlayers: [],
        playersFound: 0,
      });

      // Clear table state when leaving matchmaking
      await this.myPlayer.setMyTable(undefined);
    } catch (e) {
      this.toast.error('Fehler beim Verlassen der Matchmaking-Warteschlange');
      console.error('matchmaking/leave failed:', e);
    }
  }

  private handleMatchmakingUpdate(data: any): void {
    // Handle updates like estimated wait time, queue position, etc.
    if (data.estimatedWaitTime) {
      this._state.next({
        ...this.state,
        estimatedWaitTime: data.estimatedWaitTime,
      });
    }
  }

  private handlePartialMatch(response: any): void {
    // Handle partial matches for 3er/4er games
    console.log('partialMatch received:', response);

    // Extract data from socket response wrapper
    const data = response.data || response;
    const { table, currentPlayers, maxPlayers, type } = data;

    // Extract players from table members
    const foundPlayers: MatchmakingPlayer[] = [];
    if (table && table.members) {
      for (const member of table.members) {
        if (member && member.userID && member.userID !== 0) {
          foundPlayers.push({
            userID: member.userID,
            username: member.username,
            smallProfilePicture: member.smallProfilePicture,
            premiumBadge: member.premiumBadge,
            isGuest: member.isGuest,
          });
        }
      }
    }

    this._state.next({
      ...this.state,
      foundPlayers,
      playersFound: foundPlayers.length,
    });
  }

  private async handleMatchFound(data: any): Promise<void> {
    // Match found, player will be automatically moved to the table
    this._state.next({
      isInQueue: false,
      numPlayers: this.state.numPlayers,
      foundPlayers: [],
      playersFound: 0,
    });

    // Update player table info
    if (data.table) {
      await this.myPlayer.setMyTable(data.table);
    }
  }

  // Method to be called externally when leaving matchmaking
  public async clearQueueState(): Promise<void> {
    this._state.next({
      isInQueue: false,
      numPlayers: this.state.numPlayers,
      foundPlayers: [],
      playersFound: 0,
    });
  }

  getWaitTimeText(): string {
    if (!this.state.isInQueue || !this.state.searchStartTime) {
      return '';
    }

    const elapsed = Math.floor(
      (Date.now() - this.state.searchStartTime.getTime()) / 1000
    );
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;

    if (minutes > 0) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${seconds}s`;
  }
}
